<template>
  <div class="role-permissions-page">
    <h2>角色权限管理</h2>
    
    <div class="notice-text">
      <p>配置不同角色对各个页面的访问权限。勾选表示该角色可以访问对应页面。</p>
      <p><strong>注意：</strong>平台管理员拥有所有页面的访问权限，此权限不可修改。</p>
    </div>

    <div class="permissions-table-container">
      <table class="permissions-table">
        <thead>
          <tr>
            <th class="page-name-column">页面名称</th>
            <th class="role-column">平台管理员 <span class="fixed-badge">固定</span></th>
            <th class="role-column">管理员</th>
            <th class="role-column">普通用户</th>
            <th class="role-column">录音用户</th>
            <th class="role-column">课程管理员</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="permission in permissions" :key="permission.id">
            <td class="page-name-cell">{{ permission.pageName }}</td>
            <td class="checkbox-cell">
              <input 
                type="checkbox" 
                v-model="permission.superAdminAccess"
                :id="'super-' + permission.id"
                disabled
                class="disabled-checkbox"
              />
              <label :for="'super-' + permission.id" class="disabled-label"></label>
            </td>
            <td class="checkbox-cell">
              <input 
                type="checkbox" 
                v-model="permission.adminAccess"
                :id="'admin-' + permission.id"
              />
              <label :for="'admin-' + permission.id"></label>
            </td>
            <td class="checkbox-cell">
              <input 
                type="checkbox" 
                v-model="permission.userAccess"
                :id="'user-' + permission.id"
              />
              <label :for="'user-' + permission.id"></label>
            </td>
            <td class="checkbox-cell">
              <input 
                type="checkbox" 
                v-model="permission.recordingUserAccess"
                :id="'recording-' + permission.id"
              />
              <label :for="'recording-' + permission.id"></label>
            </td>
            <td class="checkbox-cell">
              <input 
                type="checkbox" 
                v-model="permission.courseAdminAccess"
                :id="'course-admin-' + permission.id"
              />
              <label :for="'course-admin-' + permission.id"></label>
            </td>
          </tr>
          <tr v-if="permissions.length === 0">
            <td colspan="6" class="no-data">暂无权限配置数据</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="action-buttons">
      <button @click="savePermissions" class="save-btn" :disabled="loading">
        {{ loading ? '保存中...' : '确认修改' }}
      </button>
      <button @click="resetPermissions" class="reset-btn" :disabled="loading">
        重置
      </button>
    </div>

    <div v-if="message" class="message" :class="messageType">
      {{ message }}
    </div>
  </div>
</template>

<script>
import api from '@/services/api'

export default {
  name: 'RolePermissionsPage',
  data() {
    return {
      permissions: [],
      loading: false,
      message: '',
      messageType: '',
      originalPermissions: []
    }
  },
  mounted() {
    this.loadPermissions()
  },
  methods: {
    async loadPermissions() {
      try {
        this.loading = true
        const response = await api.get('/role-permissions')
        
        // 根据API响应结构处理数据
        // 拦截器处理后，response就是 {success: true, message: "...", data: [...]}
        if (response && response.success && response.data) {
          this.permissions = response.data || []
        } else {
          this.permissions = []
        }
        
        // 保存原始数据用于重置
        this.originalPermissions = JSON.parse(JSON.stringify(this.permissions))
      } catch (error) {
        console.error('加载权限配置失败:', error)
        this.showMessage('加载权限配置失败', 'error')
      } finally {
        this.loading = false
      }
    },
    
    async savePermissions() {
      try {
        this.loading = true
        
        // 确保平台管理员权限保持为true（不能被修改）
        const permissionsToSave = this.permissions.map(permission => ({
          ...permission,
          superAdminAccess: true  // 强制设置为true
        }))
        
        const response = await api.post('/role-permissions/update', permissionsToSave)
        if (response.success) {
          this.showMessage('权限配置保存成功', 'success')
          // 更新原始数据
          this.originalPermissions = JSON.parse(JSON.stringify(this.permissions))
          
          // 通知父组件刷新权限
          this.$emit('permissions-updated')
          
          // 延迟刷新页面权限，确保后端权限配置已生效
          setTimeout(() => {
            this.refreshDashboardPermissions()
          }, 1000)
        } else {
          this.showMessage('保存失败: ' + response.message, 'error')
        }
      } catch (error) {
        console.error('保存权限配置失败:', error)
        this.showMessage('保存权限配置失败', 'error')
      } finally {
        this.loading = false
      }
    },
    
    resetPermissions() {
      this.permissions = JSON.parse(JSON.stringify(this.originalPermissions))
      this.showMessage('已重置为原始配置', 'info')
    },
    
    async refreshDashboardPermissions() {
      try {
        // 通过事件总线或直接调用父组件方法刷新权限
        if (this.$parent && this.$parent.refreshPermissions) {
          await this.$parent.refreshPermissions()
        }
      } catch (error) {
        console.error('刷新Dashboard权限失败:', error)
      }
    },
    
    showMessage(text, type) {
      this.message = text
      this.messageType = type
      setTimeout(() => {
        this.message = ''
        this.messageType = ''
      }, 3000)
    }
  }
}
</script>

<style scoped>
.role-permissions-page {
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

h2 {
  background: linear-gradient(135deg, #1e293b, #475569);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-align: center;
  margin-bottom: 2rem;
  font-size: 2.5rem;
  font-weight: 800;
}

.notice-text {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(147, 197, 253, 0.1) 100%);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  color: #1e40af;
  font-size: 1rem;
  font-weight: 500;
  backdrop-filter: blur(10px);
}

.permissions-table-container {
  background: linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%);
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.1);
  overflow: hidden;
  margin-bottom: 2rem;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255,255,255,0.2);
}

.permissions-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.9rem;
}

.permissions-table th {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-weight: 700;
  padding: 1.5rem 1rem;
  text-align: center;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.permissions-table td {
  padding: 1.25rem 1rem;
  border-bottom: 1px solid rgba(0,0,0,0.05);
  text-align: center;
  vertical-align: middle;
  transition: all 0.3s ease;
}

.permissions-table tr:hover td {
  background: linear-gradient(90deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%);
}

.page-name-column {
  width: 40%;
  text-align: left !important;
}

.role-column {
  width: 20%;
}

.page-name-cell {
  text-align: left !important;
  font-weight: 500;
  color: #333;
}

.checkbox-cell {
  text-align: center;
}

.checkbox-cell input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: #007bff;
}

.checkbox-cell label {
  cursor: pointer;
  margin-left: 8px;
  font-size: 13px;
  color: #666;
}

.action-buttons {
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 2rem;
}

.save-btn, .reset-btn {
  padding: 1rem 2rem;
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
}

.save-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.save-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%);
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
}

.save-btn:disabled {
  background: linear-gradient(135deg, #9ca3af 0%, #6b7280 100%);
  cursor: not-allowed;
  transform: none;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.reset-btn {
  background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%);
  color: white;
}

.reset-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #4b5563 0%, #374151 100%);
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(107, 114, 128, 0.4);
}

.reset-btn:disabled {
  background: linear-gradient(135deg, #d1d5db 0%, #9ca3af 100%);
  cursor: not-allowed;
  transform: none;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.message {
  padding: 12px 20px;
  border-radius: 6px;
  text-align: center;
  font-weight: 500;
  margin-top: 15px;
}

.message.success {
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.message.error {
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.message.info {
  background-color: #d1ecf1;
  color: #0c5460;
  border: 1px solid #bee5eb;
}

.no-data {
  text-align: center;
  color: #666;
  font-style: italic;
  padding: 20px;
}

.disabled-checkbox {
  opacity: 0.5;
  cursor: not-allowed;
  background-color: #f8f9fa;
}

.disabled-label {
  color: #6c757d;
  cursor: not-allowed;
}

.fixed-badge {
  background-color: #28a745;
  color: white;
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 3px;
  margin-left: 5px;
  font-weight: normal;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .role-permissions-page {
    padding: 10px;
  }
  
  .permissions-table {
    font-size: 12px;
  }
  
  .permissions-table th,
  .permissions-table td {
    padding: 8px 6px;
  }
  
  .action-buttons {
    flex-direction: column;
    align-items: center;
  }
  
  .save-btn, .reset-btn {
    width: 200px;
  }
}
</style>